import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { Toaster } from '@/componentes/ui/sonner';
import { TooltipProvider } from '@/componentes/ui/tooltip';
import { logout as logoutNaApi, obterSessao } from '@/features/auth/api';
import { renovarSessao } from '@/lib/api';
import {
  aoEncerrarSessao,
  aoExigirNovaSenha,
  ContextoAuth,
  definirAccessToken,
  encerrarSessaoLocal,
  type EstadoAuth,
  temPermissao,
} from '@/lib/auth';
import { criarQueryClient } from '@/lib/query';
import { ContextoTema, guardarTema, temaInicial, type EstadoTema, type Tema } from '@/lib/tema';
import type { Usuario } from '@/tipos/api';

function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(temaInicial);

  useEffect(() => {
    // `.dark` na raiz é o que o `@custom-variant dark` do Tailwind 4 observa (src/index.css).
    document.documentElement.classList.toggle('dark', tema === 'escuro');
    document.documentElement.dataset.tema = tema;
  }, [tema]);

  const definirTema = useCallback((novo: Tema) => {
    setTema(novo);
    guardarTema(novo);
  }, []);

  const valor = useMemo<EstadoTema>(
    () => ({
      tema,
      definirTema,
      alternarTema: () => definirTema(tema === 'escuro' ? 'claro' : 'escuro'),
    }),
    [tema, definirTema],
  );

  return <ContextoTema value={valor}>{children}</ContextoTema>;
}

/**
 * Sessão do painel. No mount tenta **restaurar** a sessão: `POST /auth/refresh` (o cookie httpOnly
 * `triplice_refresh` viaja sozinho) e, com o access token novo na memória, `GET /auth/sessao` para
 * saber quem é e o que pode. É o que faz o F5 não deslogar sem guardar nada no navegador.
 *
 * Falha = anônimo, **em silêncio**: quem nunca entrou também cai aqui, e um erro na tela seria ruído.
 */
function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  const definirSessao = useCallback((dados: { accessToken: string; usuario: Usuario }) => {
    // O token vai só para a memória (lib/auth.ts). Nada de storage.
    definirAccessToken(dados.accessToken);
    setUsuario(dados.usuario);
  }, []);

  const recarregarSessao = useCallback(async (): Promise<Usuario | null> => {
    try {
      const sessao = await obterSessao();
      setUsuario(sessao.usuario);
      return sessao.usuario;
    } catch {
      // 401 já derruba a sessão pelo interceptor; qualquer outro erro deixa o estado como estava.
      return null;
    }
  }, []);

  const encerrarSessao = useCallback(async () => {
    try {
      // Revoga a família do refresh no servidor; sem isto o cookie continuaria valendo.
      await logoutNaApi();
    } catch {
      // Servidor fora ou cookie já expirado: a sessão local sai do mesmo jeito.
    } finally {
      encerrarSessaoLocal();
    }
  }, []);

  useEffect(() => {
    let ativo = true;
    void (async () => {
      try {
        await renovarSessao();
        const sessao = await obterSessao();
        if (!ativo) return;
        setUsuario(sessao.usuario);
      } catch {
        if (!ativo) return;
        encerrarSessaoLocal();
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // O interceptor de 401 chama `encerrarSessaoLocal()` quando o refresh falha.
  useEffect(() => aoEncerrarSessao(() => setUsuario(null)), []);

  // O interceptor de 403 avisa quando o admin exigiu senha nova com a pessoa no meio do uso (1.8):
  // relida a sessão, a `RotaProtegida` leva à tela bloqueante.
  useEffect(
    () =>
      aoExigirNovaSenha(() => {
        void recarregarSessao();
      }),
    [recarregarSessao],
  );

  const valor = useMemo<EstadoAuth>(
    () => ({
      usuario,
      autenticado: usuario !== null,
      carregando,
      permissoes: usuario?.permissoes ?? [],
      tem: (permissao) => temPermissao(usuario, permissao),
      definirSessao,
      encerrarSessao,
      recarregarSessao,
    }),
    [usuario, carregando, definirSessao, encerrarSessao, recarregarSessao],
  );

  return <ContextoAuth value={valor}>{children}</ContextoAuth>;
}

/**
 * Ordem dos provedores, e por que ela é esta: QueryClient primeiro (o `AuthProvider` pode querer
 * invalidar consultas no logout), Tema antes de tudo o que renderiza (o `Toaster` lê o tema),
 * Auth antes do roteador (as rotas dependem da sessão) e Tooltip por último, envolvendo a árvore.
 */
export function Providers({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient?: QueryClient;
}) {
  const [cliente] = useState(() => queryClient ?? criarQueryClient());

  return (
    <QueryClientProvider client={cliente}>
      <TemaProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={200}>
            {children}
            {/* `aria-live` do sonner anuncia o toast a leitor de tela (CONVENCOES-UI, acessibilidade). */}
            <Toaster position="top-right" richColors closeButton />
          </TooltipProvider>
        </AuthProvider>
      </TemaProvider>
    </QueryClientProvider>
  );
}
